import React, { useState, useEffect } from 'react';
import { useParams, Outlet, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useGetAppDetails } from '../services/appService'; // Import hook
import { useAuth } from '../context/AuthContext'; // Import useAuth
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArticleIcon from '@mui/icons-material/Article';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout'; // Import Logout icon
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import MapIcon from '@mui/icons-material/Map'; // Import MapIcon
import PeopleIcon from '@mui/icons-material/People'; // Import PeopleIcon
import StorageIcon from '@mui/icons-material/Storage'; // Import StorageIcon for Knowledge Base
import ForumIcon from '@mui/icons-material/Forum'; // Import icon for Chat
import FindInPageIcon from '@mui/icons-material/FindInPage'; // Import new icon for Research
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })((
  { theme, open },
) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  marginTop: theme.spacing(8), // Offset for AppBar
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

const StyledAppBar = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const menuItems = [
  { text: 'Dashboard', path: 'dashboard', icon: <DashboardIcon /> },
  { text: 'Articles', path: 'articles', icon: <ArticleIcon /> },
  { text: 'Chat Groups', path: 'chat-groups', icon: <ChatIcon /> },
  { text: 'Events', path: 'events', icon: <EventIcon /> },
  { text: 'Map POIs', path: 'map-pois', icon: <MapIcon /> },
  { text: 'Users', path: 'users', icon: <PeopleIcon /> },
  { text: 'Organisationen', path: 'organizations', icon: <BusinessIcon /> },
  { text: 'Meine Mitglieder', path: 'org-members', icon: <GroupIcon /> },
  { text: 'Vouchers', path: 'vouchers', icon: <LocalOfferIcon /> },
  { text: 'Auswertungen', path: 'publication-ranking', icon: <LeaderboardIcon /> },
  { text: 'App Settings', path: 'settings', icon: <SettingsIcon /> },
  { text: 'Knowledge Base', path: 'knowledge-base', icon: <StorageIcon /> },
  { text: 'Chat', path: 'chat', icon: <ForumIcon /> },
  { text: 'Content Sources', path: 'ai-pipeline', icon: <FindInPageIcon /> },
];

function AppDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true); // Drawer open state
  const getAppDetailsHook = useGetAppDetails(); // Use the hook
  const { logout, currentUser } = useAuth(); // Get logout function

  useEffect(() => {
    const fetchDetails = async () => {
      if (!appId) return;
      setLoading(true);
      setError('');
      try {
        const data = await getAppDetailsHook(appId);
        setAppDetails(data);
      } catch (err) {
        console.error("Error fetching app details:", err);
        setError(err.message || 'Failed to load app details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [appId, getAppDetailsHook]); // Add hook function to dependency array

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleBackToSelection = () => {
    navigate('/select-app');
  };

  const handleLogout = () => {
    logout();
    // Navigate to login or home after logout is handled by AuthContext/PrivateRoute
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!appDetails) {
     return <Alert severity="warning">App details not found.</Alert>;
  }

  const currentPath = location.pathname.split(`/app/${appId}/`)[1] || 'dashboard';

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledAppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {appDetails.name || `App ${appId}`} - Admin Panel
          </Typography>
          <Button 
             color="inherit" 
             startIcon={<ArrowBackIcon />} 
             onClick={handleBackToSelection}
          >
            Change App
          </Button>
        </Toolbar>
      </StyledAppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems
            .filter((item) => {
              // org_admin users can only see Articles and Events
              if (currentUser?.role === 'org_admin') {
                const allowedPaths = ['dashboard', 'articles', 'events', 'org-members'];
                return allowedPaths.includes(item.path);
              }
              // Redaktion users can only see specific tabs
              if (currentUser?.role === 'redaktion') {
                const allowedPaths = ['dashboard', 'articles', 'events', 'organizations', 'users', 'publication-ranking'];
                return allowedPaths.includes(item.path);
              }
              return true; // full_admin and regular_admin see all
            })
            .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton 
                component={RouterLink} 
                to={item.path} 
                selected={currentPath === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        {/* Add Logout Button */}
         <List>
            <ListItem disablePadding>
                 <ListItemButton onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItemButton>
            </ListItem>
        </List>
      </Drawer>
      <Main open={open}>
        {/* Outlet renders the nested route components (DashboardPage, ArticlesPage, ChatGroupsPage, etc.) */}
        <Outlet context={{ appDetails }} /> 
      </Main>
    </Box>
  );
}

export default AppDetailPage; 